import { Gender, ProgramType, RangeLevel } from 'fitbeat.models';

const capTo100 = (value: number) => value > 100 ? 100 : value;
const maxWorkouts = 5;
const maxEffortPoints = 200;
const maxCalories = 1000;
const minAge = 18;

export interface IStatRange {
    minMax: number[];
    ranges: IRangeLevels;
}

export interface IBodyFatStatRange extends IStatRange {
    gender: Gender;
    longTermGoal: ProgramType;
    age: number[];
}

export interface IMuscleMassStatRange extends IStatRange {
    gender: Gender;
    longTermGoal: ProgramType;
}

export interface IRangeLevels {
    [RangeLevel.VeryLow]: IRangeInfo;
    [RangeLevel.TooLow]: IRangeInfo;
    [RangeLevel.Good]: IRangeInfo;
    [RangeLevel.High]?: IRangeInfo;
    [RangeLevel.VeryHigh]?: IRangeInfo;
}

export interface IRangeInfo {
    break?: number;
    scores?: Array<{ range: number[], level: number }>;
}

export class StatRangesConfig {
    public static getBodyFatStatRangeForUser(gender: Gender, age: number, longTermGoal: ProgramType) {
        const sex = this.getSex(gender);
        return this.bodyFatStatRanges.find((x) =>
            x.gender === sex &&
            x.longTermGoal === longTermGoal &&
            this.between(age < minAge ? minAge : age, x.age[0], x.age[1]));
    }

    public static getMuscleMassStatRangeForUser(gender: Gender, longTermGoal: ProgramType) {
        const sex = this.getSex(gender);
        return this.muscleMassStatRanges.find((x) =>
            x.gender === sex &&
            x.longTermGoal === longTermGoal);
    }

    public static getOrganAndBoneMass(gender: Gender, weight: number) {
        const sex = this.getSex(gender);
        const organAndBoneMass = this.organAndBoneMass.find((x) =>
            x.gender === sex &&
            this.between(weight, x.weight[0], x.weight[1]));

        return organAndBoneMass ? organAndBoneMass : {
            organMass: 0, // What would be a good default value?
            boneMass: 0,
        };
    }

    public static calcWorkoutGaugePercent(workouts: number) {
        const minMax = this.workoutStatRange.minMax;
        const intervalPerc = 100 / (minMax[1] - minMax[0]);
        const percent = (workouts - minMax[0]) * intervalPerc;

        return percent > 100 ? 100 : this.round(percent);
    }

    public static calcWorkoutRangeDegrees() {
        return {
            [RangeLevel[RangeLevel.VeryLow]]: this.getOverallGaugeDegree(this.workoutStatRange, RangeLevel.VeryLow),
            [RangeLevel[RangeLevel.TooLow]]: this.getOverallGaugeDegree(this.workoutStatRange, RangeLevel.TooLow),
            [RangeLevel[RangeLevel.Good]]: this.getOverallGaugeDegree(this.workoutStatRange, RangeLevel.Good),
        };
    }

    public static calcWorkoutRangePercentages() {
        return {
            [RangeLevel[RangeLevel.VeryLow]]: this.getOverallGaugePercent(this.workoutStatRange, RangeLevel.VeryLow),
            [RangeLevel[RangeLevel.TooLow]]: this.getOverallGaugePercent(this.workoutStatRange, RangeLevel.TooLow),
            [RangeLevel[RangeLevel.Good]]: this.getOverallGaugePercent(this.workoutStatRange, RangeLevel.Good),
        };
    }

    public static calcEffortPointsGaugePercent(averageEffort: number) {
        const minMax = this.effortPointsStatRange.minMax;
        const intervalPerc = 100 / (minMax[1] - minMax[0]);
        const percent = (averageEffort - minMax[0]) * intervalPerc;

        return this.round(capTo100(percent));
    }

    public static getCalcEffortPointsRanges() {
        return {
            [RangeLevel[RangeLevel.VeryLow]]: this.getBreak(this.effortPointsStatRange, RangeLevel.VeryLow),
            [RangeLevel[RangeLevel.TooLow]]: this.getBreak(this.effortPointsStatRange, RangeLevel.TooLow),
            [RangeLevel[RangeLevel.Good]]: this.getBreak(this.effortPointsStatRange, RangeLevel.Good),
        };
    }

    public static calcEffortPointsRangeDegrees() {
        return {
            [RangeLevel[RangeLevel.VeryLow]]:
                this.getOverallGaugeDegree(this.effortPointsStatRange, RangeLevel.VeryLow),
            [RangeLevel[RangeLevel.TooLow]]:
                this.getOverallGaugeDegree(this.effortPointsStatRange, RangeLevel.TooLow),
            [RangeLevel[RangeLevel.Good]]:
                this.getOverallGaugeDegree(this.effortPointsStatRange, RangeLevel.Good),
        };
    }

    public static calcEffortPointsRangePercentages() {
        return {
            [RangeLevel[RangeLevel.VeryLow]]:
                this.getOverallGaugePercent(this.effortPointsStatRange, RangeLevel.VeryLow),
            [RangeLevel[RangeLevel.TooLow]]:
                this.getOverallGaugePercent(this.effortPointsStatRange, RangeLevel.TooLow),
            [RangeLevel[RangeLevel.Good]]:
                this.getOverallGaugePercent(this.effortPointsStatRange, RangeLevel.Good),
        };
    }

    public static calcCaloriesGaugePercent(averageCalories: number) {
        const minMax = this.caloriesStatRange.minMax;
        const intervalPerc = 100 / (minMax[1] - minMax[0]);
        const percent = (averageCalories - minMax[0]) * intervalPerc;

        return this.round(capTo100(percent));
    }

    public static getCaloriesRanges() {
        return {
            [RangeLevel[RangeLevel.VeryLow]]: this.getBreak(this.caloriesStatRange, RangeLevel.VeryLow),
            [RangeLevel[RangeLevel.TooLow]]: this.getBreak(this.caloriesStatRange, RangeLevel.TooLow),
            [RangeLevel[RangeLevel.Good]]: this.getBreak(this.caloriesStatRange, RangeLevel.Good),
        };
    }

    public static calcCaloriesRangeDegrees() {
        return {
            [RangeLevel[RangeLevel.VeryLow]]:
                this.getOverallGaugeDegree(this.caloriesStatRange, RangeLevel.VeryLow),
            [RangeLevel[RangeLevel.TooLow]]:
                this.getOverallGaugeDegree(this.caloriesStatRange, RangeLevel.TooLow),
            [RangeLevel[RangeLevel.Good]]:
                this.getOverallGaugeDegree(this.caloriesStatRange, RangeLevel.Good),
        };
    }

    public static calcCaloriesRangePercentages() {
        return {
            [RangeLevel[RangeLevel.VeryLow]]:
                this.getOverallGaugePercent(this.caloriesStatRange, RangeLevel.VeryLow),
            [RangeLevel[RangeLevel.TooLow]]:
                this.getOverallGaugePercent(this.caloriesStatRange, RangeLevel.TooLow),
            [RangeLevel[RangeLevel.Good]]:
                this.getOverallGaugePercent(this.caloriesStatRange, RangeLevel.Good),
        };
    }

    public static calcBodyFatRangeDegrees(bodyFatStatRange: IBodyFatStatRange | undefined) {
        if (!bodyFatStatRange) {
            return;
        }
        return {
            [RangeLevel[RangeLevel.VeryLow]]: this.getOverallGaugeDegree(bodyFatStatRange, RangeLevel.VeryLow),
            [RangeLevel[RangeLevel.TooLow]]: this.getOverallGaugeDegree(bodyFatStatRange, RangeLevel.TooLow),
            [RangeLevel[RangeLevel.Good]]: this.getOverallGaugeDegree(bodyFatStatRange, RangeLevel.Good),
            [RangeLevel[RangeLevel.High]]: this.getOverallGaugeDegree(bodyFatStatRange, RangeLevel.High),
            [RangeLevel[RangeLevel.VeryHigh]]: this.getOverallGaugeDegree(bodyFatStatRange, RangeLevel.VeryHigh),
        };
    }

    public static calcBodyWeightRanges(
        bodyFatStatRange: IBodyFatStatRange | undefined, weight: number, fatlessWeight: number) {
        if (!bodyFatStatRange) {
            return;
        }
        return {
            [RangeLevel[RangeLevel.VeryLow]]:
                this.getDerivedBodyWeight(bodyFatStatRange, RangeLevel.VeryLow, weight, fatlessWeight),
            [RangeLevel[RangeLevel.TooLow]]:
                this.getDerivedBodyWeight(bodyFatStatRange, RangeLevel.TooLow, weight, fatlessWeight),
            [RangeLevel[RangeLevel.Good]]:
                this.getDerivedBodyWeight(bodyFatStatRange, RangeLevel.Good, weight, fatlessWeight),
            [RangeLevel[RangeLevel.High]]:
                this.getDerivedBodyWeight(bodyFatStatRange, RangeLevel.High, weight, fatlessWeight),
            [RangeLevel[RangeLevel.VeryHigh]]:
                this.getDerivedBodyWeight(bodyFatStatRange, RangeLevel.VeryHigh, weight, fatlessWeight),
        };
    }

    public static getBodyFatRanges(bodyFatStatRange: IBodyFatStatRange | undefined) {
        if (!bodyFatStatRange) {
            return;
        }
        return {
            [RangeLevel[RangeLevel.VeryLow]]: this.getBreak(bodyFatStatRange, RangeLevel.VeryLow),
            [RangeLevel[RangeLevel.TooLow]]: this.getBreak(bodyFatStatRange, RangeLevel.TooLow),
            [RangeLevel[RangeLevel.Good]]: this.getBreak(bodyFatStatRange, RangeLevel.Good),
            [RangeLevel[RangeLevel.High]]: this.getBreak(bodyFatStatRange, RangeLevel.High),
            [RangeLevel[RangeLevel.VeryHigh]]: this.getBreak(bodyFatStatRange, RangeLevel.VeryHigh),
        };
    }

    public static calcBodyFatRangePercentages(bodyFatStatRange: IBodyFatStatRange | undefined) {
        if (!bodyFatStatRange) {
            return;
        }
        return {
            [RangeLevel[RangeLevel.VeryLow]]: this.getOverallGaugePercent(bodyFatStatRange, RangeLevel.VeryLow),
            [RangeLevel[RangeLevel.TooLow]]: this.getOverallGaugePercent(bodyFatStatRange, RangeLevel.TooLow),
            [RangeLevel[RangeLevel.Good]]: this.getOverallGaugePercent(bodyFatStatRange, RangeLevel.Good),
            [RangeLevel[RangeLevel.High]]: this.getOverallGaugePercent(bodyFatStatRange, RangeLevel.High),
            [RangeLevel[RangeLevel.VeryHigh]]: this.getOverallGaugePercent(bodyFatStatRange, RangeLevel.VeryHigh),
        };
    }

    public static getMuscleMassRanges(muscleMassStatRange: IMuscleMassStatRange | undefined) {
        if (!muscleMassStatRange) {
            return;
        }
        return {
            [RangeLevel[RangeLevel.VeryLow]]: this.getBreak(muscleMassStatRange, RangeLevel.VeryLow),
            [RangeLevel[RangeLevel.TooLow]]: this.getBreak(muscleMassStatRange, RangeLevel.TooLow),
            [RangeLevel[RangeLevel.Good]]: this.getBreak(muscleMassStatRange, RangeLevel.Good),
        };
    }

    public static getMuscleMassKgRanges(muscleMassStatRange: IMuscleMassStatRange | undefined, height: number) {
        if (!muscleMassStatRange) {
            return;
        }
        return {
            [RangeLevel[RangeLevel.VeryLow]]:
                this.getMuscleMassKgBreak(muscleMassStatRange, RangeLevel.VeryLow, height),
            [RangeLevel[RangeLevel.TooLow]]: this.getMuscleMassKgBreak(muscleMassStatRange, RangeLevel.TooLow, height),
            [RangeLevel[RangeLevel.Good]]: this.getMuscleMassKgBreak(muscleMassStatRange, RangeLevel.Good, height),
        };
    }

    public static calcMuscleMassRangePercentages(muscleMassStatRange: IMuscleMassStatRange | undefined) {
        if (!muscleMassStatRange) {
            return;
        }
        return {
            [RangeLevel[RangeLevel.VeryLow]]: this.getOverallGaugePercent(muscleMassStatRange, RangeLevel.VeryLow),
            [RangeLevel[RangeLevel.TooLow]]: this.getOverallGaugePercent(muscleMassStatRange, RangeLevel.TooLow),
            [RangeLevel[RangeLevel.Good]]: this.getOverallGaugePercent(muscleMassStatRange, RangeLevel.Good),
        };
    }

    public static calcMuscleMassRangeDegrees(muscleMassStatRange: IMuscleMassStatRange | undefined) {
        if (!muscleMassStatRange) {
            return;
        }
        return {
            [RangeLevel[RangeLevel.VeryLow]]: this.getOverallGaugeDegree(muscleMassStatRange, RangeLevel.VeryLow),
            [RangeLevel[RangeLevel.TooLow]]: this.getOverallGaugeDegree(muscleMassStatRange, RangeLevel.TooLow),
            [RangeLevel[RangeLevel.Good]]: this.getOverallGaugeDegree(muscleMassStatRange, RangeLevel.Good),
        };
    }

    public static calcBodyFatGaugePercent(bodyFatStatRange: IBodyFatStatRange | undefined, bodyFat: number) {
        if (!bodyFatStatRange) { return; }
        if (!bodyFat) { return 0; }
        const minMax = bodyFatStatRange.minMax;
        const intervalPerc = 100 / (minMax[1] - minMax[0]);
        const result = (bodyFat - minMax[0]) * intervalPerc;

        return this.round(capTo100(result));
    }

    public static calcBodyWeightGaugePercent(
        bodyFatStatRange: IBodyFatStatRange | undefined, weight: number, fatlessWeight: number) {
        if (!bodyFatStatRange) { return; }
        const min = fatlessWeight;
        const max = this.getDerivedBodyWeight(bodyFatStatRange, RangeLevel.VeryHigh, weight, fatlessWeight);
        const intervalPerc = 100 / (max - min);
        const result = (weight - min) * intervalPerc;

        return this.round(capTo100(result));
    }

    public static calcMuscleMassGaugePercent(
        muscleMassStatRange: IMuscleMassStatRange | undefined, muscleMassMeter: number) {
        if (!muscleMassStatRange) { return; }
        if (muscleMassMeter === 0) { return 0; }
        const min = muscleMassStatRange.minMax[0];
        const max = muscleMassStatRange.minMax[1];
        const result = ((muscleMassMeter - min) * 100) / (max - min);
        return capTo100(result);
    }

    public static calcBodyFatRangePercent(bodyFatStatRange: IBodyFatStatRange | undefined, bodyFat: number) {
        if (!bodyFatStatRange) {
            return;
        }
        const ranges = bodyFatStatRange.ranges;
        const bucket: any = Object.keys(ranges).reduce((result, key) => {
            if (this.between(bodyFat, ranges[key].minMax[0], ranges[key].minMax[1])) {
                result = {
                    range: RangeLevel[key],
                    min: ranges[key].minMax[0],
                    max: ranges[key].minMax[1],
                };
            }
            return result;
        }, {});

        const intervalPerc = 100 / (bucket.max - bucket.min);
        const percent = Math.round((bodyFat - bucket.min) * intervalPerc);

        return {
            range: bucket.range,
            percent,
        };
    }

    private static readonly bodyFatStatRanges: IBodyFatStatRange[] = [
        // Male - GainMuscleAndStrengthGoal
        {
            gender: Gender.Male,
            age: [18, 30],
            longTermGoal: ProgramType.GainMuscleAndStrengthGoal,
            minMax: [3, 31],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 8,
                },
                [RangeLevel.Good]: {
                    break: 15,
                },
                [RangeLevel.High]: {
                    break: 21,
                },
                [RangeLevel.VeryHigh]: {
                    // break: maxMaleBodyFat(31),
                    break: 31,
                },
            },
        },
        {
            gender: Gender.Male,
            age: [31, 50],
            longTermGoal: ProgramType.GainMuscleAndStrengthGoal,
            minMax: [3, 35],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 8,
                },
                [RangeLevel.Good]: {
                    break: 18,
                },
                [RangeLevel.High]: {
                    break: 25,
                },
                [RangeLevel.VeryHigh]: {
                    break: 35,
                },
            },
        },
        {
            gender: Gender.Male,
            age: [51, Number.MAX_SAFE_INTEGER],
            longTermGoal: ProgramType.GainMuscleAndStrengthGoal,
            minMax: [3, 36],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 10,
                },
                [RangeLevel.Good]: {
                    break: 23,
                },
                [RangeLevel.High]: {
                    break: 29,
                },
                [RangeLevel.VeryHigh]: {
                    break: 36,
                },
            },
        },

        // Female - GainMuscleAndStrengthGoal
        {
            gender: Gender.Female,
            age: [18, 30],
            longTermGoal: ProgramType.GainMuscleAndStrengthGoal,
            minMax: [8, 37],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 12,
                },
                [RangeLevel.TooLow]: {
                    break: 14,
                },
                [RangeLevel.Good]: {
                    break: 25,
                },
                [RangeLevel.High]: {
                    break: 30,
                },
                [RangeLevel.VeryHigh]: {
                    break: 37,
                },
            },
        },
        {
            gender: Gender.Female,
            age: [31, 50],
            longTermGoal: ProgramType.GainMuscleAndStrengthGoal,
            minMax: [8, 39],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 12,
                },
                [RangeLevel.TooLow]: {
                    break: 14,
                },
                [RangeLevel.Good]: {
                    break: 28,
                },
                [RangeLevel.High]: {
                    break: 32,
                },
                [RangeLevel.VeryHigh]: {
                    break: 39,
                },
            },
        },
        {
            gender: Gender.Female,
            age: [51, Number.MAX_SAFE_INTEGER],
            longTermGoal: ProgramType.GainMuscleAndStrengthGoal,
            minMax: [8, 43],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 15,
                },
                [RangeLevel.TooLow]: {
                    break: 16,
                },
                [RangeLevel.Good]: {
                    break: 32,
                },
                [RangeLevel.High]: {
                    break: 36,
                },
                [RangeLevel.VeryHigh]: {
                    break: 43,
                },
            },
        },

        // Male - Mobility and Tone
        {
            gender: Gender.Male,
            age: [18, 30],
            longTermGoal: ProgramType.MobilityAndTone,
            minMax: [3, 31],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 8,
                },
                [RangeLevel.Good]: {
                    break: 15,
                },
                [RangeLevel.High]: {
                    break: 21,
                },
                [RangeLevel.VeryHigh]: {
                    break: 31,
                },
            },
        },
        {
            gender: Gender.Male,
            age: [31, 50],
            longTermGoal: ProgramType.MobilityAndTone,
            minMax: [3, 35],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 8,
                },
                [RangeLevel.Good]: {
                    break: 18,
                },
                [RangeLevel.High]: {
                    break: 25,
                },
                [RangeLevel.VeryHigh]: {
                    break: 35,
                },
            },
        },
        {
            gender: Gender.Male,
            age: [51, Number.MAX_SAFE_INTEGER],
            longTermGoal: ProgramType.MobilityAndTone,
            minMax: [3, 36],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 10,
                },
                [RangeLevel.Good]: {
                    break: 23,
                },
                [RangeLevel.High]: {
                    break: 29,
                },
                [RangeLevel.VeryHigh]: {
                    break: 36,
                },
            },
        },

        // Female - Mobility and Tone
        {
            gender: Gender.Female,
            age: [18, 30],
            longTermGoal: ProgramType.MobilityAndTone,
            minMax: [8, 37],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 12,
                },
                [RangeLevel.TooLow]: {
                    break: 14,
                },
                [RangeLevel.Good]: {
                    break: 25,
                },
                [RangeLevel.High]: {
                    break: 30,
                },
                [RangeLevel.VeryHigh]: {
                    break: 37,
                },
            },
        },
        {
            gender: Gender.Female,
            age: [31, 50],
            longTermGoal: ProgramType.MobilityAndTone,
            minMax: [8, 39],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 12,
                },
                [RangeLevel.TooLow]: {
                    break: 14,
                },
                [RangeLevel.Good]: {
                    break: 28,
                },
                [RangeLevel.High]: {
                    break: 32,
                },
                [RangeLevel.VeryHigh]: {
                    break: 39,
                },
            },
        },
        {
            gender: Gender.Female,
            age: [51, Number.MAX_SAFE_INTEGER],
            longTermGoal: ProgramType.MobilityAndTone,
            minMax: [8, 43],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 14,
                },
                [RangeLevel.TooLow]: {
                    break: 16,
                },
                [RangeLevel.Good]: {
                    break: 32,
                },
                [RangeLevel.High]: {
                    break: 36,
                },
                [RangeLevel.VeryHigh]: {
                    break: 43,
                },
            },
        },

        // Male - All Rounder
        {
            gender: Gender.Male,
            age: [18, 30],
            longTermGoal: ProgramType.GetStrongAndFitGoal,
            minMax: [3, 31],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 8,
                },
                [RangeLevel.Good]: {
                    break: 15,
                },
                [RangeLevel.High]: {
                    break: 21,
                },
                [RangeLevel.VeryHigh]: {
                    break: 31,
                },
            },
        },
        {
            gender: Gender.Male,
            age: [31, 50],
            longTermGoal: ProgramType.GetStrongAndFitGoal,
            minMax: [3, 35],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 8,
                },
                [RangeLevel.Good]: {
                    break: 18,
                },
                [RangeLevel.High]: {
                    break: 25,
                },
                [RangeLevel.VeryHigh]: {
                    break: 35,
                },
            },
        },
        {
            gender: Gender.Male,
            age: [51, Number.MAX_SAFE_INTEGER],
            longTermGoal: ProgramType.GetStrongAndFitGoal,
            minMax: [3, 36],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 10,
                },
                [RangeLevel.Good]: {
                    break: 23,
                },
                [RangeLevel.High]: {
                    break: 29,
                },
                [RangeLevel.VeryHigh]: {
                    break: 36,
                },
            },
        },

        // Female - All Rounder
        {
            gender: Gender.Female,
            age: [18, 30],
            longTermGoal: ProgramType.GetStrongAndFitGoal,
            minMax: [8, 37],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 12,
                },
                [RangeLevel.TooLow]: {
                    break: 14,
                },
                [RangeLevel.Good]: {
                    break: 25,
                },
                [RangeLevel.High]: {
                    break: 30,
                },
                [RangeLevel.VeryHigh]: {
                    break: 37,
                },
            },
        },
        {
            gender: Gender.Female,
            age: [31, 50],
            longTermGoal: ProgramType.GetStrongAndFitGoal,
            minMax: [8, 39],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 12,
                },
                [RangeLevel.TooLow]: {
                    break: 14,
                },
                [RangeLevel.Good]: {
                    break: 28,
                },
                [RangeLevel.High]: {
                    break: 32,
                },
                [RangeLevel.VeryHigh]: {
                    break: 39,
                },
            },
        },
        {
            gender: Gender.Female,
            age: [51, Number.MAX_SAFE_INTEGER],
            longTermGoal: ProgramType.GetStrongAndFitGoal,
            minMax: [8, 43],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 14,
                },
                [RangeLevel.TooLow]: {
                    break: 16,
                },
                [RangeLevel.Good]: {
                    break: 32,
                },
                [RangeLevel.High]: {
                    break: 36,
                },
                [RangeLevel.VeryHigh]: {
                    break: 43,
                },
            },
        },

        // Male - Lean & Fit
        {
            gender: Gender.Male,
            age: [18, 30],
            longTermGoal: ProgramType.GetLeanAndFitGoal,
            minMax: [3, 30],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 8,
                },
                [RangeLevel.Good]: {
                    break: 14,
                },
                [RangeLevel.High]: {
                    break: 20,
                },
                [RangeLevel.VeryHigh]: {
                    break: 30,
                },
            },
        },
        {
            gender: Gender.Male,
            age: [31, 50],
            longTermGoal: ProgramType.GetLeanAndFitGoal,
            minMax: [3, 34],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 8,
                },
                [RangeLevel.Good]: {
                    break: 17,
                },
                [RangeLevel.High]: {
                    break: 24,
                },
                [RangeLevel.VeryHigh]: {
                    break: 34,
                },
            },
        },
        {
            gender: Gender.Male,
            age: [51, Number.MAX_SAFE_INTEGER],
            longTermGoal: ProgramType.GetLeanAndFitGoal,
            minMax: [3, 35],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 10,
                },
                [RangeLevel.Good]: {
                    break: 22,
                },
                [RangeLevel.High]: {
                    break: 28,
                },
                [RangeLevel.VeryHigh]: {
                    break: 35,
                },
            },
        },

        // Female - Lean & Fit
        {
            gender: Gender.Female,
            age: [18, 30],
            longTermGoal: ProgramType.GetLeanAndFitGoal,
            minMax: [8, 36],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 12,
                },
                [RangeLevel.TooLow]: {
                    break: 14,
                },
                [RangeLevel.Good]: {
                    break: 24,
                },
                [RangeLevel.High]: {
                    break: 29,
                },
                [RangeLevel.VeryHigh]: {
                    break: 36,
                },
            },
        },
        {
            gender: Gender.Female,
            age: [31, 50],
            longTermGoal: ProgramType.GetLeanAndFitGoal,
            minMax: [8, 38],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 12,
                },
                [RangeLevel.TooLow]: {
                    break: 14,
                },
                [RangeLevel.Good]: {
                    break: 27,
                },
                [RangeLevel.High]: {
                    break: 31,
                },
                [RangeLevel.VeryHigh]: {
                    break: 38,
                },
            },
        },
        {
            gender: Gender.Female,
            age: [51, Number.MAX_SAFE_INTEGER],
            longTermGoal: ProgramType.GetLeanAndFitGoal,
            minMax: [8, 42],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 14,
                },
                [RangeLevel.TooLow]: {
                    break: 16,
                },
                [RangeLevel.Good]: {
                    break: 31,
                },
                [RangeLevel.High]: {
                    break: 35,
                },
                [RangeLevel.VeryHigh]: {
                    break: 42,
                },
            },
        },

        // Male - Muscle & Shred
        {
            gender: Gender.Male,
            age: [18, 30],
            longTermGoal: ProgramType.MuscleAndShred,
            minMax: [3, 30],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 8,
                },
                [RangeLevel.Good]: {
                    break: 14,
                },
                [RangeLevel.High]: {
                    break: 20,
                },
                [RangeLevel.VeryHigh]: {
                    break: 30,
                },
            },
        },
        {
            gender: Gender.Male,
            age: [31, 50],
            longTermGoal: ProgramType.MuscleAndShred,
            minMax: [3, 34],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 8,
                },
                [RangeLevel.Good]: {
                    break: 17,
                },
                [RangeLevel.High]: {
                    break: 24,
                },
                [RangeLevel.VeryHigh]: {
                    break: 34,
                },
            },
        },
        {
            gender: Gender.Male,
            age: [51, Number.MAX_SAFE_INTEGER],
            longTermGoal: ProgramType.MuscleAndShred,
            minMax: [3, 35],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 6,
                },
                [RangeLevel.TooLow]: {
                    break: 10,
                },
                [RangeLevel.Good]: {
                    break: 22,
                },
                [RangeLevel.High]: {
                    break: 28,
                },
                [RangeLevel.VeryHigh]: {
                    break: 35,
                },
            },
        },

        // Female - Muscle & Shred
        {
            gender: Gender.Female,
            age: [18, 30],
            longTermGoal: ProgramType.MuscleAndShred,
            minMax: [8, 36],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 12,
                },
                [RangeLevel.TooLow]: {
                    break: 14,
                },
                [RangeLevel.Good]: {
                    break: 24,
                },
                [RangeLevel.High]: {
                    break: 29,
                },
                [RangeLevel.VeryHigh]: {
                    break: 36,
                },
            },
        },
        {
            gender: Gender.Female,
            age: [31, 50],
            longTermGoal: ProgramType.MuscleAndShred,
            minMax: [8, 38],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 12,
                },
                [RangeLevel.TooLow]: {
                    break: 14,
                },
                [RangeLevel.Good]: {
                    break: 27,
                },
                [RangeLevel.High]: {
                    break: 31,
                },
                [RangeLevel.VeryHigh]: {
                    break: 38,
                },
            },
        },
        {
            gender: Gender.Female,
            age: [51, Number.MAX_SAFE_INTEGER],
            longTermGoal: ProgramType.MuscleAndShred,
            minMax: [8, 42],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 14,
                },
                [RangeLevel.TooLow]: {
                    break: 16,
                },
                [RangeLevel.Good]: {
                    break: 31,
                },
                [RangeLevel.High]: {
                    break: 35,
                },
                [RangeLevel.VeryHigh]: {
                    break: 42,
                },
            },
        },
    ];

    private static readonly muscleMassStatRanges: IMuscleMassStatRange[] = [
        {
            gender: Gender.Male,
            longTermGoal: ProgramType.GainMuscleAndStrengthGoal,
            minMax: [19, 40],
            ranges: {
                [RangeLevel.Good]: {
                    break: 40,
                },
                [RangeLevel.TooLow]: {
                    break: 30,
                },
                [RangeLevel.VeryLow]: {
                    break: 25,
                },
            },
        },
        {
            gender: Gender.Male,
            longTermGoal: ProgramType.GetStrongAndFitGoal,
            minMax: [18, 38],
            ranges: {
                [RangeLevel.Good]: {
                    break: 38,
                },
                [RangeLevel.TooLow]: {
                    break: 28,
                },
                [RangeLevel.VeryLow]: {
                    break: 24,
                },
            },
        },
        {
            gender: Gender.Male,
            longTermGoal: ProgramType.MobilityAndTone,
            minMax: [18, 38],
            ranges: {
                [RangeLevel.Good]: {
                    break: 38,
                },
                [RangeLevel.TooLow]: {
                    break: 28,
                },
                [RangeLevel.VeryLow]: {
                    break: 24,
                },
            },
        },
        {
            gender: Gender.Male,
            longTermGoal: ProgramType.GetLeanAndFitGoal,
            minMax: [18, 38],
            ranges: {
                [RangeLevel.Good]: {
                    break: 38,
                },
                [RangeLevel.TooLow]: {
                    break: 28,
                },
                [RangeLevel.VeryLow]: {
                    break: 24,
                },
            },
        },
        {
            gender: Gender.Male,
            longTermGoal: ProgramType.MuscleAndShred,
            minMax: [18, 38],
            ranges: {
                [RangeLevel.Good]: {
                    break: 38,
                },
                [RangeLevel.TooLow]: {
                    break: 28,
                },
                [RangeLevel.VeryLow]: {
                    break: 24,
                },
            },
        },

        {
            gender: Gender.Female,
            longTermGoal: ProgramType.GainMuscleAndStrengthGoal,
            minMax: [15, 33],
            ranges: {
                [RangeLevel.Good]: {
                    break: 33,
                },
                [RangeLevel.TooLow]: {
                    break: 23,
                },
                [RangeLevel.VeryLow]: {
                    break: 21,
                },
            },
        },
        {
            gender: Gender.Female,
            longTermGoal: ProgramType.GetStrongAndFitGoal,
            minMax: [14, 32],
            ranges: {
                [RangeLevel.Good]: {
                    break: 32,
                },
                [RangeLevel.TooLow]: {
                    break: 22,
                },
                [RangeLevel.VeryLow]: {
                    break: 20,
                },
            },
        },
        {
            gender: Gender.Female,
            longTermGoal: ProgramType.MobilityAndTone,
            minMax: [14, 32],
            ranges: {
                [RangeLevel.Good]: {
                    break: 32,
                },
                [RangeLevel.TooLow]: {
                    break: 22,
                },
                [RangeLevel.VeryLow]: {
                    break: 20,
                },
            },
        },
        {
            gender: Gender.Female,
            longTermGoal: ProgramType.GetLeanAndFitGoal,
            minMax: [14, 32],
            ranges: {
                [RangeLevel.Good]: {
                    break: 32,
                },
                [RangeLevel.TooLow]: {
                    break: 22,
                },
                [RangeLevel.VeryLow]: {
                    break: 20,
                },
            },
        },
        {
            gender: Gender.Female,
            longTermGoal: ProgramType.MuscleAndShred,
            minMax: [14, 32],
            ranges: {
                [RangeLevel.Good]: {
                    break: 32,
                },
                [RangeLevel.TooLow]: {
                    break: 22,
                },
                [RangeLevel.VeryLow]: {
                    break: 20,
                },
            },
        },
    ];

    private static readonly organAndBoneMass = [
        {
            gender: Gender.Male,
            weight: [0, 65],
            organMass: 4.9,
            boneMass: 2.65,
        },
        {
            gender: Gender.Male,
            weight: [65, 95],
            organMass: 4.9,
            boneMass: 3.29,
        },
        {
            gender: Gender.Male,
            weight: [95, Number.MAX_SAFE_INTEGER],
            organMass: 4.9,
            boneMass: 3.69,
        },

        {
            gender: Gender.Female,
            weight: [0, 50],
            organMass: 4.2,
            boneMass: 1.95,
        },
        {
            gender: Gender.Female,
            weight: [50, 75],
            organMass: 4.2,
            boneMass: 2.40,
        },
        {
            gender: Gender.Female,
            weight: [76, Number.MAX_SAFE_INTEGER],
            organMass: 4.2,
            boneMass: 2.95,
        },
    ];

    private static readonly workoutStatRange: IStatRange =
        {
            minMax: [0, maxWorkouts],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 1,
                },
                [RangeLevel.TooLow]: {
                    break: 3,
                },
                [RangeLevel.Good]: {
                    break: maxWorkouts,
                },
            },
        };

    private static readonly effortPointsStatRange: IStatRange =
        {
            minMax: [0, maxEffortPoints],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 50,
                },
                [RangeLevel.TooLow]: {
                    break: 100,
                },
                [RangeLevel.Good]: {
                    break: maxEffortPoints,
                },
            },
        };

    private static readonly caloriesStatRange: IStatRange =
        {
            minMax: [0, maxCalories],
            ranges: {
                [RangeLevel.VeryLow]: {
                    break: 200,
                },
                [RangeLevel.TooLow]: {
                    break: 500,
                },
                [RangeLevel.Good]: {
                    break: maxCalories,
                },
            },
        };

    private static between(x: number, min: number, max: number) {
        return x >= min && x <= max;
    }

    private static getBreak(statRange: IStatRange, rangeLevel: RangeLevel) {
        const range: IRangeInfo | undefined = statRange.ranges[rangeLevel];
        if (!range) { return 0; }
        const cutoff = range.break ? range.break : 0;
        return cutoff;
    }

    private static getOverallGaugePercent(statRange: IStatRange, rangeLevel: RangeLevel) {
        const range: IRangeInfo | undefined = statRange.ranges[rangeLevel];
        if (!range) { return 0; }
        const min = statRange.minMax[0];
        const max = statRange.minMax[1];
        const cutoff = range.break ? range.break : 0;
        const result = ((cutoff - min) * 100) / (max - min);

        return this.round(result);
    }

    private static getOverallGaugeDegree(
        statRange: IStatRange, rangeLevel: RangeLevel, degreeThreshold: number = 270) {
        const rangePercentage = this.getOverallGaugePercent(statRange, rangeLevel) / 100;
        return Math.round(rangePercentage * degreeThreshold);
    }

    private static getDerivedBodyWeight(
        bodyFatStatRange: IBodyFatStatRange, rangeLevel: RangeLevel, weight: number, fatlessWeight: number) {
        const range: IRangeInfo | undefined = bodyFatStatRange.ranges[rangeLevel];
        if (!range) { return 0; }
        const cutoff = range.break ? range.break : 0;
        const derivedBodyWeight = fatlessWeight + (weight * (cutoff / 100));
        return this.round(derivedBodyWeight);
    }

    private static getMuscleMassKgBreak(muscleMassStatRange: IStatRange, rangeLevel: RangeLevel, height: number) {
        return this.round(this.getBreak(muscleMassStatRange, rangeLevel) * (height / 100));
    }

    private static round(val: number) {
        return Math.round(val * 10) / 10;
    }

    private static getSex(gender: Gender) {
        let sex = gender;
        if (gender === Gender.Other) {
            sex = Gender.Female;
        }
        return sex;
    }
}
